import type { ReactNode } from 'react'
import { Box, Flex, Text } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { useFormColors } from './FormThemeContext'
import { ACCENT, ACCENT_SOFT, FONT_DISPLAY, FONT_MONO } from '../tokens'

export type SidebarStepStatus = 'approved' | 'observed' | 'inReview' | 'pending'

interface SidebarStepConfig {
  key: string
  label: string
  icon?: ReactNode
  hasError?: boolean
  status?: SidebarStepStatus
  observationCount?: number
}

interface WizardSidebarProps<T extends string> {
  title: string
  subtitle?: string
  steps: SidebarStepConfig[]
  activeStep: T
  completedSteps: Set<string>
  onStepClick: (step: T) => void
}

const SEALED = '#2a8a87'
const OBSERVED = '#d49a2a'

/**
 * El asistente como RECORRIDO: una línea une los pasos y se llena hasta el paso
 * actual; los completados quedan "sellados" (teal + check), el activo late en el
 * mineral del módulo y su resaltado se desliza al cambiar de paso.
 */
export function WizardSidebar<T extends string>({
  title,
  subtitle,
  steps,
  activeStep,
  completedSteps,
  onStepClick,
}: WizardSidebarProps<T>) {
  const c = useFormColors()
  const activeIndex = Math.max(0, steps.findIndex(s => s.key === activeStep))
  const done = steps.filter(s => s.status === 'approved' || completedSteps.has(s.key)).length
  const pct = steps.length > 1 ? activeIndex / (steps.length - 1) : 0

  return (
    <Box
      as="aside"
      w="276px"
      minW="276px"
      flexShrink={0}
      py={5}
      display="flex"
      flexDirection="column"
      borderRight="1px solid"
      borderColor={c.border}
      bg={c.bgCard}
    >
      {/* Encabezado + avance */}
      <Box px={5} mb={5}>
        <Text fontFamily={FONT_DISPLAY} fontSize="19px" fontWeight="800" color={c.text} letterSpacing="-0.02em" lineHeight="1.15">
          {title}
        </Text>
        {subtitle && <Text fontSize="12.5px" color={c.textMuted} mt={1}>{subtitle}</Text>}
        <Flex mt={3} align="center" gap={2.5}>
          <Box flex={1} h="6px" borderRadius="6px" bg={c.border} overflow="hidden">
            <motion.div
              initial={false}
              animate={{ width: `${(done / Math.max(steps.length, 1)) * 100}%` }}
              transition={{ type: 'spring', stiffness: 200, damping: 28 }}
              style={{ height: '100%', background: SEALED, borderRadius: 6 }}
            />
          </Box>
          <Text fontFamily={FONT_MONO} fontSize="11px" color={c.textMuted}>{done}/{steps.length}</Text>
        </Flex>
      </Box>

      {/* Recorrido */}
      <Box as="nav" flex={1} overflowY="auto" px={3}>
        <Box position="relative">
        {/* Línea base y línea recorrida (hasta el paso actual): van de nodo a nodo */}
        <Box position="absolute" left="31px" top="22px" bottom="22px" w="2px" bg={c.border} borderRadius="2px" />
        <motion.div
          initial={false}
          animate={{ scaleY: pct }}
          transition={{ type: 'spring', stiffness: 160, damping: 26 }}
          style={{ position: 'absolute', left: 31, top: 22, bottom: 22, width: 2, borderRadius: 2, background: ACCENT, transformOrigin: 'top' }}
        />

        {steps.map((step, i) => {
          const isActive = activeStep === step.key
          const isSealed = step.status === 'approved' || completedSteps.has(step.key)
          const isObserved = step.status === 'observed'
          const nodeBg = isActive ? ACCENT : isSealed ? SEALED : isObserved ? OBSERVED : '#fffdf9'
          const nodeFg = isActive || isSealed || isObserved ? '#fff' : c.textMuted

          return (
            <Flex
              key={step.key}
              as="button" {...{ type: 'button' }}
              position="relative"
              w="100%"
              alignItems="center"
              gap={3}
              pl={2.5}
              pr={2}
              py={2.5}
              mb={0.5}
              borderRadius="12px"
              cursor="pointer"
              textAlign="left"
              onClick={() => onStepClick(step.key as T)}
              _hover={isActive ? {} : { bg: c.bgHover }}
              transition="background .15s"
            >
              {isActive && (
                <motion.span
                  layoutId="k-wizard-active"
                  transition={{ type: 'spring', stiffness: 480, damping: 38 }}
                  style={{ position: 'absolute', inset: 0, borderRadius: 12, background: ACCENT_SOFT }}
                />
              )}

              {/* Nodo del recorrido */}
              <Box position="relative" flexShrink={0} w="24px" h="24px">
                {isActive && (
                  <motion.span
                    animate={{ scale: [1, 1.7], opacity: [0.5, 0] }}
                    transition={{ duration: 1.6, repeat: Infinity, ease: 'easeOut' }}
                    style={{ position: 'absolute', inset: 0, borderRadius: 999, background: ACCENT }}
                  />
                )}
                <Flex position="absolute" inset={0} borderRadius="full" align="center" justify="center"
                  bg={nodeBg} color={nodeFg} border="2px solid" borderColor={isActive || isSealed || isObserved ? 'transparent' : c.border}
                  fontFamily={FONT_MONO} fontSize="10.5px" fontWeight="600" transition="background .25s, color .25s">
                  {isSealed && !isActive ? (
                    <motion.svg initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 500, damping: 18 }}
                      width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </motion.svg>
                  ) : isObserved && !isActive ? (
                    step.observationCount && step.observationCount > 0 ? step.observationCount : '!'
                  ) : (
                    i + 1
                  )}
                </Flex>
              </Box>

              {/* Ícono + rótulo */}
              <Flex position="relative" flex={1} align="center" gap={2} minW={0}
                color={isActive ? c.text : isSealed ? c.textSecondary : c.textMuted}>
                {step.icon && <Box as="span" display="flex" opacity={isActive ? 1 : 0.7} fontSize="16px">{step.icon}</Box>}
                <Text as="span" fontSize="13.5px" fontWeight={isActive ? 700 : 500} lineClamp={1}>{step.label}</Text>
              </Flex>

              {step.status === 'inReview' && (
                <Box position="relative" w="10px" h="10px" borderRadius="full" border="2px solid" borderColor={c.textMuted} flexShrink={0} aria-label="En revisión" />
              )}
              {!step.status && step.hasError && (
                <Box position="relative" w="7px" h="7px" borderRadius="full" bg={c.error} flexShrink={0} />
              )}
            </Flex>
          )
        })}
        </Box>
      </Box>
    </Box>
  )
}
